import { useEffect, useMemo, useState } from "react";
import useTrackView from "../../hooks/useTrackView";
import { useParams } from "react-router-dom";
import { UserLayout } from "./components/layout/UserLayout";
import Container from "./components/Container";
import Text from "./components/Text";
import Img from "./components/Img";
import Effects from "./components/Effects";
import { getImg } from "../../Components/Utils";
import BlogCard from "./components/cards/BlogCard";
import { buildEntityMeta } from "../../hooks/useFrontendMeta";
import {
  Tag,
  ArrowLeft,
  ArrowRight,
  Link2,
  Calendar,
  User,
  Clock,
  Quote
} from "lucide-react";
import {
  FacebookShareButton, FacebookIcon,
  TwitterShareButton, XIcon,
  LinkedinShareButton, LinkedinIcon,
  WhatsappShareButton, WhatsappIcon
} from 'react-share';
import { useFetchBlogDetailPageQuery } from "../../stores/api/pages.api";

const HISTORY_KEY = "blog_nav_history";

const readHistory = () => {
  try {
    return JSON.parse(sessionStorage.getItem(HISTORY_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const writeHistory = (list) => {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
};

const useBlogNavHistory = (slug, title) => {
  const [history, setHistory] = useState(readHistory);

  useEffect(() => {
    if (!slug || !title) return;
    const current = readHistory();
    const idx = current.findIndex((e) => e.slug === slug);
    let next;
    if (idx >= 0) {
      next = [...current];
      next[idx] = { slug, title };
    } else {
      next = [...current, { slug, title }].slice(-50);
    }
    writeHistory(next);
    setHistory(next);
  }, [slug, title]);

  return useMemo(() => {
    const idx = history.findIndex((e) => e.slug === slug);
    if (idx === -1) return { prev: null, next: null };
    return {
      prev: idx > 0 ? history[idx - 1] : null,
      next: idx < history.length - 1 ? history[idx + 1] : null,
    };
  }, [history, slug]);
};

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const BlogDetailSkeleton = () => (
  <UserLayout>
    <div className="w-full h-[40vh] sm:h-[550px] bg-gray-900" />
    <Container className="py-8 md:py-12 max-w-7xl mx-auto px-4">
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </Container>
  </UserLayout>
);

// Individual Share Button Component to safely isolate hover states and render perfectly
// Individual Share Button Component styled to match image_acb5cf.png exactly
const CustomShareButton = ({ Button, Icon, url, brandColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Button
      url={url}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-200"
      style={{
        backgroundColor: brandColor,
        borderColor: "#E5E7EB",
      }}
    >
      <Icon
        round={true}
        size={24}
        bgStyle={{ fill: "transparent" }}
        iconFillColor={isHovered ? "#fff" : "#fff"}
      />
    </Button>
  );
};

export const BlogDetail = () => {
  const { slug } = useParams();
  const { data, isLoading } = useFetchBlogDetailPageQuery(slug);
  const { prev, next } = useBlogNavHistory(slug, data?.post?.title);
  useTrackView("blog", data?.post?.id);

  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) return <BlogDetailSkeleton />;

  const post = data?.post;
  const related = data?.related ?? [];

  if (!post) {
    return (
      <UserLayout>
        <Container className="py-16 md:py-24 text-center max-w-md mx-auto px-4">
          <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm">
            <Text is="p" className="text-gray-500 font-medium">Post not found</Text>
          </div>
        </Container>
      </UserLayout>
    );
  }

  // Per-post SEO meta: prefer admin-entered meta_*, fall back to derived values.
  const plainContent = (post.content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const blogMeta = buildEntityMeta(post, {
    fallbackTitle: `${post.title} | Ukasha Enterprise Blog`,
    fallbackDescription: plainContent.slice(0, 160) || post.title,
    fallbackKeywords: [post.title, ...(post.tags ?? [])].filter(Boolean).join(", "),
    ogType: "article",
    ogImage: getImg(post.img),
  });

  return (
    <UserLayout meta={blogMeta}>
      <article className="w-full">

        {/* 1. Immersive Fluid Hero Canvas (Fully Scalable for Mobile) */}
        <div className="relative w-full aspect-[3/1.2]  bg-gray-950 overflow-hidden group">

          {/* Background Image Container */}
          <div className="absolute inset-0 z-0">
            <Img
              src={getImg(post.img)}
              alt={post.title}
              className="w-full h-full object-cover opacity-90 transition-transform duration-1000 ease-out group-hover:scale-[1.01]"
            />
            {/* Strictly Bottom Focus Linear Mask */}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" />
          </div>

          {/* Premium Fine-line Grid Overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none z-10"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)`,
              backgroundSize: '24px 24px'
            }}
          />

          {/* Title Position Deck */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-end pb-8 md:pb-16 px-4 max-w-5xl mx-auto text-center">
            {post.tags?.[0] && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-widest bg-blue-600 text-white mb-3 shadow-sm">
                {post.tags[0]}
              </span>
            )}
            <h1 className="text-white text-2xl  md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.25] mb-4 md:mb-6 max-w-4xl drop-shadow-md font-sans text-center uppercase"
            >
              {post.title}
            </h1>

            {/* Micro Functional Info Bar */}
            <div className="flex flex-wrap items-center justify-center gap-y-2 gap-x-4 sm:gap-x-5 text-[11px] sm:text-xs text-gray-200 font-medium pt-3 md:pt-4 border-t border-white/10 w-full max-w-xl">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 sm:w-5 h-5 bg-white/20 text-white font-bold rounded-full flex items-center justify-center text-[8px] sm:text-[9px] backdrop-blur-sm">
                  {post.author ? post.author.charAt(0).toUpperCase() : <User size={8} />}
                </div>
                <span>{post.author || "Staff Writer"}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar size={12} className="text-gray-300" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={12} className="text-gray-300" />
                <span>5 min read</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Main Space Layout (Responsive Grid Orders) */}
        <Container className="py-8 md:py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Effects type="scroll-fadeup">
            <div className="relative flex flex-col lg:grid lg:grid-cols-[1fr_80px] gap-8 lg:gap-10 items-start">

              {/* Main Text Container Track */}
              <div className="w-full min-w-0 bg-white rounded-2xl order-1">

                {/* Standard Compact Quote Deck */}
                {post.blockquote && (
                  <div className="relative bg-gradient-to-r from-slate-50 to-transparent border-l-4 border-blue-600 p-4 md:p-5 my-5 rounded-r-xl">
                    <Quote className="absolute right-3 top-3 text-gray-200/40 w-6 h-6 pointer-events-none" />
                    <Text
                      is="blockquote"
                      className="relative z-10 text-gray-700 italic text-[13px] sm:text-base leading-relaxed font-serif"
                    >
                      "{post.blockquote}"
                    </Text>
                  </div>
                )}

                {/* Dynamic Content Showcasing Images */}
                {post.images?.length > 0 && (
                  <div className={`grid gap-3 md:gap-4 my-6 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                    {post.images.map((img, i) => (
                      <div key={i} className="rounded-xl overflow-hidden border border-gray-100 shadow-sm group bg-gray-50 aspect-[16/10]">
                        <Img
                          src={getImg(img)}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Highly Balanced & Crisp Compact Typography Viewport */}
                <div
                      className="tiptap-content prose prose-slate max-w-none text-gray-600 text-sm sm:text-base leading-relaxed
                      prose-headings:text-gray-900 prose-headings:font-bold prose-headings:leading-snug
                      prose-h2:text-xl sm:prose-h2:text-3xl prose-h2:mt-6 prose-h2:mb-3 
                      prose-h3:text-lg sm:prose-h3:text-xl prose-h3:mt-4 prose-h3:mb-2
                      
                      /* Paragraphs aur list spacing fix ki hai */
                      prose-p:mb-4 prose-p:leading-7 
                      prose-ul:my-4 prose-ol:my-4
                      prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                      px-0"
                      dangerouslySetInnerHTML={{
                        __html: post.content,
                      }}
                    />

                {/* Micro Rounded Tag Track */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap pt-4 mt-6 border-t border-gray-100">
                    <Tag size={11} className="text-gray-400 mr-0.5 flex-shrink-0" />
                    {post.tags.map((tag) => (
                      <Text
                        key={tag}
                        is="a"
                        href="#"
                        className="text-[9px] sm:text-[10px] font-medium text-gray-500 bg-gray-50 border border-gray-200 px-2.5 py-0.5 rounded-full hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-200"
                      >
                        {tag}
                      </Text>
                    ))}
                  </div>
                )}
              </div>

              {/* Flex Toolbar Layer Panel */}
              <aside className="order-2 lg:order-2 lg:sticky lg:top-28 flex flex-row lg:flex-col items-center justify-center gap-2.5 md:gap-3 py-3 px-4 bg-gray-50/60 border border-gray-200/50 rounded-2xl w-full lg:w-auto shadow-none mt-2 lg:mt-0">
                {[
                  { Button: FacebookShareButton, Icon: FacebookIcon, brandColor: "#1877F2" },
                  { Button: TwitterShareButton, Icon: XIcon, brandColor: "#000000" },
                  { Button: LinkedinShareButton, Icon: LinkedinIcon, brandColor: "#0A66C2" },
                  { Button: WhatsappShareButton, Icon: WhatsappIcon, brandColor: "#25D366" },
                ].map((item, idx) => (
                  <CustomShareButton
                    key={idx}
                    Button={item.Button}
                    Icon={item.Icon}
                    url={shareUrl}
                    brandColor={item.brandColor}
                  />
                ))}

                <button
                  onClick={handleCopyLink}
                  className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white border flex items-center justify-center shadow-xs transition-all duration-200 relative ${copied
                      ? 'border-green-500 text-green-600 bg-green-50/20'
                      : 'border-gray-200 text-gray-400 hover:text-gray-900 hover:border-gray-400'
                    }`}
                  title="Copy link"
                >
                  <Link2 size={13} />
                  {copied && (
                    <span className="absolute -top-7 lg:top-auto lg:-left-14 bg-gray-900 text-white text-[8px] font-bold px-1 py-0.5 rounded shadow-sm whitespace-nowrap">
                      Copied!
                    </span>
                  )}
                </button>
              </aside>

            </div>

            {/* Compact Internal Nav Navigation Controls */}
            {(prev || next) && (
              <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 mt-8 pt-5 border-t border-gray-200">
                {prev ? (
                  <Text
                    is="a"
                    href={`/blog/${prev.slug}`}
                    className="flex items-center gap-2.5 group p-3 border border-gray-100 rounded-xl hover:border-blue-300 hover:bg-gray-50/20 transition-all duration-200 text-left w-full"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-200">
                      <ArrowLeft size={12} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block">
                        Previous
                      </span>
                      <span className="text-[11px] sm:text-xs font-medium text-gray-800 group-hover:text-blue-600 transition-colors block truncate">
                        {prev.title}
                      </span>
                    </div>
                  </Text>
                ) : (
                  <div className="hidden sm:block" />
                )}

                {next && (
                  <Text
                    is="a"
                    href={`/blog/${next.slug}`}
                    className="flex items-center justify-between gap-2.5 group p-3 border border-gray-100 rounded-xl hover:border-blue-300 hover:bg-gray-50/20 transition-all duration-200 text-right w-full"
                  >
                    <div className="min-w-0 flex-1 text-left sm:text-right">
                      <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 block">
                        Next
                      </span>
                      <span className="text-[11px] sm:text-xs font-medium text-gray-800 group-hover:text-blue-600 transition-colors block truncate">
                        {next.title}
                      </span>
                    </div>
                    <div className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 order-first sm:order-last group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-200">
                      <ArrowRight size={12} />
                    </div>
                  </Text>
                )}
              </div>
            )}
          </Effects>

          {/* Related Items Section Block */}
          {related.length > 0 && (
            <Effects type="scroll-fadeup">
              <div className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-gray-200">
                <div className="flex flex-col items-center mb-6 md:mb-8 text-center">
                  <span className="text-[8px] font-bold text-blue-600 uppercase tracking-widest mb-1">More Articles</span>
                  <Text
                    is="h3"
                    className="text-gray-900 text-base md:text-xl font-bold tracking-tight uppercase"
                  >
                    Keep Reading Related Articles
                  </Text>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {related.map((p, i) => (
                    <div key={i} className="hover:-translate-y-1 transition-transform duration-300 h-full">
                      <BlogCard
                        img={p.img}
                        title={p.title}
                        description={p.description ?? ""}
                        href={`/blog/${p.slug}`}
                        category={p.category?.name ?? p.category ?? null}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Effects>
          )}
        </Container>
      </article>
    </UserLayout>
  );
};